/**
 * https://github.com/philipstanislaus/performant-array-to-tree/blob/master/src/arrayToTree.ts
 */

export type TreeItem<T> = {
  id: string;
  parentId: string;
  value: T;
  children: Array<TreeItem<T>>;
};

export interface Config<T> {
  id: (item: T) => string;
  parentId: (item: T) => string;
}

const defaultConfig: Config<any> = {
  id: () => "id",
  parentId: () => "parentId"
};

/**
 * Unflattens an array to a tree with runtime O(n)
 */
export function arrayToTree<T>(
  items: T[],
  config: Partial<Config<T>> = {}
): Array<TreeItem<T>> {
  const conf: Config<T> = { ...defaultConfig, ...config };

  // the resulting unflattened tree
  const rootItems: Array<TreeItem<T>> = [];

  // stores all already processed items with ther ids as key so we can easily look them up
  const lookup: { [id: string]: TreeItem<T> } = {};

  // idea of this loop:
  // whenever an item has a parent, but the parent is not yet in the lookup object, we store a preliminary parent
  // in the lookup object and fill it with the data of the parent later
  // if an item has no parentId, add it as a root element to rootItems
  for (const item of items) {
    const itemId = conf.id(item);
    const parentId = conf.parentId(item);

    // look whether item already exists in the lookup table
    if (!lookup[itemId]) {
      lookup[itemId] = { children: [], value: item, id: itemId, parentId };
    }

    // add the current item's data to the item in the lookup table
    lookup[itemId] = {
      value: item,
      children: lookup[itemId].children,
      id: itemId,
      parentId
    };

    const treeItem = lookup[itemId];

    if (!parentId) {
      rootItems.push(treeItem);
    } else {
      // look whether the parent already exists in the lookup table
      if (!lookup[parentId]) {
        lookup[parentId] = { value: null, children: [], id: parentId, parentId: null };
      }

      // add the current item to the parent
      lookup[parentId].children.push(treeItem);
    }
  }

  return rootItems;
}

export function filterTreeDataByString<T>(
  data: Array<TreeItem<T>>,
  searchValue = "",
  getValue: (item: T) => string
) {
  return data.filter((item) => {
    if (item.children) {
      item.children = filterTreeDataByString(item.children, searchValue, getValue);
    }

    const value = getValue(item.value);

    return (
      value.toLowerCase().indexOf(searchValue.toLowerCase()) !== -1 ||
      (item.children && item.children.length > 0)
    );
  });
}

export function filterTreeData<T>(
  data: Array<TreeItem<T>>,
  filterFn: (item: T) => boolean
) {
  return data.filter((item) => {
    if (item.children) {
      item.children = filterTreeData(item.children, filterFn);
    }

    return filterFn(item.value) || (item.children && item.children.length > 0);
  });
}

export function treeToArray<T>(data: Array<TreeItem<T>>): T[] {
  if (!data) {
    return [];
  }

  return data.flatMap((x) => [x.value, ...treeToArray(x.children)]);
}
